import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link as RouterLink, useNavigate } from "@tanstack/react-router";
import { formatCurrency, formatNumber, truncateText } from "@/utils/format";

import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContestDialog } from "./ContestDialog";
import CreatorLandingScreen from "./CreatorLandingScreen";
import CreatorMakeVideo from "./CreatorMakeVideo";
import { Id } from "@cvx/_generated/dataModel";
import { NewPostDialog } from "./NewPostDialog";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { SimpleTotalVideosCard } from "./TotalVideosCard";
import StatusBadge from "@/components/Generic/StatusBadge";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TopPerformersCard } from "./TopPerformersCard";
import { TotalVideosCard } from "./TotalVideosCard";
import { User } from "~/types";
import VoicepalChallengeGettingStarted from "../SpecialLaunchComponents/VoicepalChallengeGettingStarted";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

export default function CreatorDashboard({
  user,
  showCreate,
}: {
  user: User | undefined | null;
  showCreate?: boolean;
}) {
  const { contractId } = useAccount();
  const [isAdviceModalOpen, setIsAdviceModalOpen] = useState(false);

  const navigate = useNavigate();

  const selectedContract = user?.creatorPartnerships.find(
    (partnership) => partnership._id === contractId,
  );

  const { data: dashboardData, refetch: refetchDashboardData } = useQuery({
    ...convexQuery(api.dashboard.getCreatorDashboard, {
      contractId: contractId as Id<"creatorContracts">,
    }),
    enabled: !!contractId,
  });

  // Handler for unpublished videos click - navigate to videos overview or show message
  const handleUnpublishedVideosClick = () => {
    navigate({
      to: "/partnerships/$partnershipId",
      params: { partnershipId: contractId as Id<"creatorContracts"> },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: "videos",
      }),
    });
  };

  // Handler for AI content creation - navigate to dashboard with create parameter
  const handleAIContentCreation = () => {
    navigate({
      to: "/dashboard?create=true",
    });
  };

  // Check if campaign has ended or contract is completed/terminated
  const isPartnershipInactive =
    dashboardData?.campaign?.status === "ended" ||
    selectedContract?.status === "completed" ||
    selectedContract?.status === "terminated";

  // Show CreatorLandingScreen when no videos are posted, or CreatorMakeVideo when showCreate is true
  if (dashboardData?.videos.length === 0 || showCreate) {
    // If partnership is inactive, show the inactive message instead of creation tools
    if (isPartnershipInactive) {
      return (
        <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
          <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center">
            <div className="max-w-md text-center space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-primary">
                  Partnership{" "}
                  {selectedContract?.status === "completed"
                    ? "Ended"
                    : selectedContract?.status === "terminated"
                      ? "Terminated"
                      : "Ended"}
                </h2>
                <p className="text-primary/60">
                  {selectedContract?.status === "completed"
                    ? "This partnership has ended. You can view your historical performance or contact the app owner to renew."
                    : selectedContract?.status === "terminated"
                      ? "This partnership has been terminated. You can view your historical performance or contact the app owner to renew."
                      : "This campaign has ended. Thank you for your participation."}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({
                      to: "/partnerships/$partnershipId",
                      params: { partnershipId: selectedContract?._id },
                    })
                  }
                  disabled={!selectedContract?._id}
                  className="flex items-center gap-2"
                >
                  View Partnership Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // If showCreate is true and partnership is active, show the video creation component
    if (showCreate) {
      return (
        <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
          <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col">
            {/* Action Buttons in Top Right - positioned as before */}
            <div className="flex justify-end items-center gap-2 relative bottom-36">
              {selectedContract?.campaign._id ===
                "nd7f46a8fdvef2b71s7m4tpgkn7kr5cg" && (
                <Button
                  variant="link"
                  onClick={() =>
                    window.open(
                      "https://calendly.com/melina-at-ali-abdaal/voicepal-creator-office-hours?month=2025-07&date=2025-07-17",
                      "_blank",
                    )
                  }
                  disabled={!selectedContract?._id}
                  className="flex items-center gap-2"
                >
                  Book Office Hours with Ali's Head of Socials ✨
                </Button>
              )}
              <Button
                onClick={() => navigate({ to: "/dashboard", replace: true })}
                variant="outline"
                className="flex items-center gap-2"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <NewPostDialog
                campaign={selectedContract?.campaign}
                contract={selectedContract}
                refetchDashboardData={refetchDashboardData}
                user={user}
              />
            </div>

            {/* CreatorMakeVideo Component */}
            <CreatorMakeVideo app={selectedContract?.app} user={user} />
          </div>
        </div>
      );
    }

    // If no videos and not showCreate, show the landing screen
    return (
      <CreatorLandingScreen
        campaign={selectedContract?.campaign}
        app={selectedContract?.app}
        contract={selectedContract}
        user={user}
        refetchDashboardData={refetchDashboardData}
      />
    );
  }

  return (
    <div className="flex h-full w-full bg-secondary px-6 pt-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        {/* Stats Section */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 relative bottom-6">
          {/* Custom Total Earnings Card */}
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-primary/60">
                Total Earnings
              </h3>
              {selectedContract?.app.logo && (
                <img
                  src={selectedContract.app.logo}
                  alt={selectedContract.app.name}
                  className="h-6 w-6 rounded-full"
                />
              )}
            </div>
            <div className="space-y-3">
              <p className="text-3xl font-semibold text-primary">
                {formatCurrency(
                  (dashboardData?.earningsFlatFee ?? 0) +
                    (dashboardData?.earningsPerVideoBonus ?? 0) +
                    (dashboardData?.earningsCPM ?? 0),
                )}
              </p>

              {/* Budget Progress Bar - only show if campaign has a prizePool */}
              {dashboardData?.campaign?.prizePool && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-primary/60">Campaign Budget</span>
                    <span className="text-primary/60">
                      {formatCurrency(dashboardData.remainingBudget ?? 0)} left
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(
                          100,
                          ((dashboardData.totalCampaignEarnings ?? 0) /
                            dashboardData.campaign.prizePool) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-primary/60">
                    <span>
                      {formatCurrency(dashboardData.totalCampaignEarnings ?? 0)}{" "}
                      earned
                    </span>
                    <span>
                      {formatCurrency(dashboardData.campaign.prizePool)} total
                    </span>
                  </div>
                </div>
              )}

              {dashboardData?.videos.some(
                (video) => video.status && video.status !== "approved",
              ) && (
                <p className="text-xs text-primary/50">
                  Only earnings from approved videos are counted
                </p>
              )}
            </div>
          </div>

          {dashboardData?.campaign?.type === "CiR" ||
          dashboardData?.campaign?.type === "challenge" ? (
            <SimpleTotalVideosCard
              publishedCount={dashboardData?.videos.length ?? 0}
              logo={selectedContract?.app.logo}
            />
          ) : (
            <TotalVideosCard
              publishedCount={dashboardData?.videos.length ?? 0}
              videoStatusCounts={{
                draft: dashboardData?.videoStatusCounts?.draft ?? 0,
                review: dashboardData?.videoStatusCounts?.review ?? 0,
                approved: dashboardData?.videoStatusCounts?.approved ?? 0,
                needs_revision: dashboardData?.videoStatusCounts
                  ? "needs_revision" in dashboardData.videoStatusCounts
                    ? (
                        dashboardData.videoStatusCounts as unknown as {
                          needs_revision: number;
                        }
                      ).needs_revision
                    : "rejected" in dashboardData.videoStatusCounts
                      ? (
                          dashboardData.videoStatusCounts as unknown as {
                            rejected: number;
                          }
                        ).rejected
                      : 0
                  : 0,
              }}
              logo={selectedContract?.app.logo}
              onUnpublishedClick={handleUnpublishedVideosClick}
            />
          )}
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-primary/60">
                View Analytics
              </h3>
              {selectedContract?.app.logo && (
                <img
                  src={selectedContract.app.logo}
                  alt={selectedContract.app.name}
                  className="h-6 w-6 rounded-full"
                />
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary/60">Total Views</span>
                <span className="text-xl font-bold text-primary">
                  {formatNumber(dashboardData?.totalViews ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-primary/60">
                  Median Views Per Video
                </span>
                <span className="text-xl font-bold text-primary">
                  {formatNumber(Math.round(dashboardData?.medianViews ?? 0))}
                </span>
              </div>
              {dashboardData?.videos.some(
                (video) => video.status && video.status !== "approved",
              ) && (
                <p className="text-xs text-primary/50 mt-2">
                  Only views from approved videos are counted
                </p>
              )}
            </div>
          </div>
          <TopPerformersCard videos={dashboardData?.videos ?? []} />
        </div>

        {/* Affiliate Coupons Section */}
        {/* {user?.creator && (
          <div className="absolute top-36 left-1/2 -translate-x-1/2">
            <CreatorAffiliateCoupons creatorId={user.creator._id} />
          </div>
        )} */}

        {/* Table Section */}
        <div className="rounded-lg border border-border bg-card relative bottom-12">
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-medium text-primary">
                Content Overview
              </h2>
            </div>

            <div className="flex items-center gap-2">
              {selectedContract?.campaign._id ===
                "nd7f46a8fdvef2b71s7m4tpgkn7kr5cg" && (
                <Button
                  variant="link"
                  onClick={() => setIsAdviceModalOpen(true)}
                  disabled={!selectedContract?._id}
                  className="flex items-center gap-2"
                >
                  Ali's team's advice ✨
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() =>
                  navigate({
                    to: "/partnerships/$partnershipId",
                    params: { partnershipId: selectedContract?._id },
                  })
                }
                disabled={!selectedContract?._id}
                className="flex items-center gap-2"
              >
                View Partnership
              </Button>
              {selectedContract?.status === "active" &&
                !isPartnershipInactive && (
                  <Button
                    onClick={handleAIContentCreation}
                    disabled={!selectedContract?._id}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Help me create ⭐
                  </Button>
                )}
              <NewPostDialog
                campaign={selectedContract?.campaign}
                contract={selectedContract}
                refetchDashboardData={refetchDashboardData}
                user={user}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <TooltipProvider>
              <table className="w-full">
                <thead className="border-b border-border bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      App
                    </th>
                    <th className="w-16 px-6 py-3 text-center text-sm font-medium text-primary/60">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-primary/60">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Views
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Likes
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Bookmarks
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Shares
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Comments
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Earnings
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Date Posted
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dashboardData?.videos.map((post) => {
                    return (
                      <tr key={post._id} className="hover:bg-secondary/50">
                        <td className="px-6 py-4">
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                            title={post.title}
                          >
                            {truncateText(post.title ?? "No title")}
                          </a>
                        </td>
                        <td className="px-6 py-4">
                          <RouterLink
                            to="/partnerships/$partnershipId"
                            params={{ partnershipId: post.contractId }}
                            className="hover:underline"
                          >
                            {selectedContract?.app.name}
                          </RouterLink>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            <PlatformIcon platform={post.platform ?? ""} />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <StatusBadge
                              status={post.status || "pending"}
                              clickable={false}
                              video={post}
                              campaignType={dashboardData?.campaign?.type}
                            />
                            {(post.status === "rejected" ||
                              post.status === "ai_rejected" ||
                              post.status === "team_rejected") && (
                              <ContestDialog
                                video={post}
                                onContestSubmitted={refetchDashboardData}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {formatNumber(post.views)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {formatNumber(post.likes)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {post.platform === "youtube" ||
                          post.platform === "youtube-shorts" ||
                          post.platform === "tiktok"
                            ? "N/A"
                            : formatNumber(post.bookmarks)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {post.platform === "youtube" ||
                          post.platform === "youtube-shorts"
                            ? "N/A"
                            : formatNumber(post.shares)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {formatNumber(post.comments)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-primary">
                          <span
                            className={
                              post.status === "rejected"
                                ? "line-through opacity-60"
                                : ""
                            }
                          >
                            {formatCurrency(post.earnings || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          {post.postDate
                            ? format(new Date(post.postDate), "dd/MM/yy")
                            : ""}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-primary">
                          <div className="flex flex-col items-end">
                            <span>
                              {post.lastUpdated
                                ? format(new Date(post.lastUpdated), "dd/MM/yy")
                                : "-"}
                            </span>
                            {post.postDate && (
                              <span className="text-xs text-primary/50 mt-1">
                                {(() => {
                                  const daysSincePost = Math.floor(
                                    (Date.now() -
                                      new Date(post.postDate).getTime()) /
                                      (1000 * 60 * 60 * 24),
                                  );
                                  return daysSincePost > 7
                                    ? "Views stopped counting"
                                    : `Views count for ${7 - daysSincePost} more days`;
                                })()}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TooltipProvider>
          </div>
        </div>

        {/* Ali's Team Advice Modal */}
        <Dialog open={isAdviceModalOpen} onOpenChange={setIsAdviceModalOpen}>
          <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ali's Team's Creator Guide</DialogTitle>
              <DialogDescription>
                Complete guide to getting started with the Voicepal Creator
                Challenge
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <VoicepalChallengeGettingStarted />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
